<template>
	<div>

		<v-row>
			<v-col>
				<ui-box
						class="mb-8"
						:form="formRef"
						newForms
				>
					<form-title :title="formTitle">
						{{ formText }}
						<template #button>
							<v-btn
									class="mt-1 rounded-lg"
									color="secondary"
									depressed
									outlined
									small
									@click="addFaq"
							>
								<v-icon>
									mdi-plus
								</v-icon>
								Add FAQ
							</v-btn>
						</template>
					</form-title>

					<ui-box class="mt-4" no-padding>

						<template v-if="faqData.length === 0">
							<div
									class="align-center d-flex justify-center w-100"
									style="height: 80px;"
							>
								Oops, no FAQs yet!
							</div>
						</template>
						<template v-else>
							<v-container style="line-height: 1rem;">
								<v-row no-gutters class="mt-n4 mb-4">
									<v-col>
										<div
												class="d-flex flex-column flex-md-row py-4"
												:class="{
													'align-center': $vuetify.breakpoint.mdAndUp,
													'justify-end': !title
												}"
										>

											<div v-if="title" class="flex-grow-1">
												<h5 class="ml-0 mt-0">{{ title }}</h5>
											</div>

											<v-divider v-if="title" class="d-block d-md-none mt-4"></v-divider>

											<div
													class="align-center d-flex pt-4 pt-md-0"
													:class="{
														'align-self-end': $vuetify.breakpoint.smAndDown
													}"
											>
												<div class="pr-4">
													<label class="v-label text-no-wrap theme--light text-body-2">
														Show All on Mobile Pass
													</label>
												</div>

												<v-checkbox
														v-model="selectAll"
														class="mt-0"
														:color="selectSome ? 'grey' : null"
														dense
														hide-details
														:indeterminate="selectSome"
														style="margin-right: -6px;"
												/>
											</div>
										</div>
									</v-col>
								</v-row>
								<draggable
										v-model="faqData"
										class="my-n4 sortables"
										direction="vertical"
										:filter="filterElements"
										:preventOnFilter="false"
										tag="div"
										@end="onDragEnd"
										@start="onDragStart"
								>
									<transition-group
											name="flip-list"
											type="transition"
									>
										<v-row
												v-for="(faq, faqIdx) in this.faqData"
												:key="faq.uid"
												no-gutters
										>
											<v-col cols="12" v-if="faq.mobile">
												<v-row no-gutters class="mb-5 mb-md-3">
													<v-chip
															color="red lighten-5"
															label
															light
															small
															style="margin-left: 30px"
													>
													<span class="red--text">
														Mobile only
													</span>
													</v-chip>
												</v-row>
											</v-col>
											<v-col
													class="flex-grow-0 flex-shrink-1 d-flex"
													cols="12"
													style="min-width: 100px; max-width: 100%"
											>
												<div :style="{
													minWidth: '30px',
													paddingTop: $vuetify.breakpoint.smAndDown ? '7px' : '16px' 
												}">
													<v-icon>mdi-drag-vertical</v-icon>
												</div>
												<div class="flex-grow-1">
													<v-col>
														<v-row>
															<v-text-field
																	v-model="faq.question"
																	class="rounded-lg"
																	:dense="$vuetify.breakpoint.smAndDown"
																	:disabled="!!faq.readOnly"
																	hide-details="auto"
																	label="Question"
																	outlined
																	persistent-hint
																	:rules="rules.question"
																	type="text"
																	@input="onChange"
															></v-text-field>
														</v-row>
														<v-row class="mt-8">
															<wysiwyg-input
																	v-if="!faq.code"
																	v-model="faq.answer"
																	:counter="500"
																	:disabled="!!disableWysiwygs"
																	label="Answer"
																	:rules="rules.answer"
																	@input="onChange"
																	@focus="onChange"
															></wysiwyg-input>
															<v-textarea
																	v-else
																	v-model="faq.answer"
																	auto-grow
																	class="rounded-lg mb-4"
																	:dense="$vuetify.breakpoint.smAndDown"
																	:disabled="!!faq.readOnly"
																	hide-details="auto"
																	label="Answer"
																	no-resize
																	outlined
																	rows="1"
																	:rules="rules.answer"
																	style="max-height: 200px;"
																	type="text"
																	@input="onChange"
															></v-textarea>
														</v-row>
													</v-col>
												</div>
												<div
														class="pl-4 pl-md-8"
														:style="{
															maxWidth: $vuetify.breakpoint.smAndDown ? '44px' : '108px',
															paddingTop: $vuetify.breakpoint.smAndDown ? '5px' : '14px'
														}"
												>

													<div
															class="align-center d-flex flex-wrap-reverse flex-md-nowrap">
														<v-btn
																color="error"
																depressed
																:disabled="faq.readOnly"
																fab
																outlined
																small
																@click="removeFaq(index)"
														>
															<v-icon>
																mdi-trash-can-outline
															</v-icon>
														</v-btn>

														<v-divider
																:class="{ 
																	'mx-2 my-1': $vuetify.breakpoint.mdAndUp,
																	'mx-1 my-2': $vuetify.breakpoint.smAndDown
																}"
																:vertical="$vuetify.breakpoint.mdAndUp"
														></v-divider>

														<v-btn
																:color="faq.showOnMobilePass ? 'primary' : 'grey'"
																depressed
																:disabled="faq.readOnly"
																fab
																:input-value="faq.showOnMobilePass"
																outlined
																small
																@click="faq.showOnMobilePass = !faq.showOnMobilePass"
														>
															<v-icon
																	v-if="faq.showOnMobilePass"
																	:key="`mobile-on-${faqIdx}`"
															>
																mdi-cellphone
															</v-icon>
															<v-icon
																	v-else
																	:key="`mobile-off-${faqIdx}`"
															>
																mdi-cellphone-off
															</v-icon>
														</v-btn>
													</div>

												</div>
											</v-col>
										</v-row>
									</transition-group>
								</draggable>
							</v-container>
						</template>
					</ui-box>
				</ui-box>
			</v-col>
		</v-row>
		<confirm-dialog
				:open="removeFaqDialog"
				description="Are you sure? This can't be undone."
				title="Remove FAQ"
				@cancel="cancelRemoveFaq"
				@confirm="doRemoveFaq"
		></confirm-dialog>
	</div>
</template>

<script>
import ConfirmDialog from '@app/Shared/UI/ConfirmDialog.vue';
import draggable from 'vuedraggable';
import { helpers } from '../helpers';

export default {
	name: 'FaqEditor',
	components: {
		ConfirmDialog,
		draggable
	},
	props: {
		faqs: {
			type: Array,
			default: () => []
		},
		formRef: {
			type: Object,
			default: () => {
			}
		},
		formText: {
			type: String,
			default: ''
		},
		formTitle: String,
		title: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			disableWysiwygs: false,
			faqData: [],
			originalContentEditableStates: [], // To store the original states
			removeFaqIndex: false,
			rules: {
				answer: [v => !!v && helpers.getCharacterCount(v) > 0 || 'Answer is required.'],
				question: [v => !!v || 'Question is required.'],
			},
			selectAll: false
		};
	},
	created() {
		this.updateFaqs(this.faqs);
		this.selectAll = this.faqData.every(d => !!d.showOnMobilePass);
	},
	computed: {
		removeFaqDialog: {
			get() {
				return this.removeFaqIndex !== false;
			},
			set(v) {
				this.removeFaqIndex = v;
			}
		},
		selectSome: {
			get() {
				return this.faqData.some(d => !!d.showOnMobilePass)
						&& !this.faqData.every(d => !!d.showOnMobilePass);
			},
			set(v) {
				if(v === true) {
					this.faqData.forEach(d => d.showOnMobilePass);
				}
				else {
					this.faqData.forEach(d => !d.showOnMobilePass);
				}
			}
		}
	},
	methods: {
		addFaq() {
			const newFaq = new Faq({ showOnMobilePass: this.selectAll });
			this.faqData.unshift(newFaq);
			this.onChange();
		},
		cancelRemoveFaq() {
			this.removeFaqDialog = false;
		},
		doRemoveFaq() {
			this.faqData.splice(this.removeFaqIndex, 1);
			this.removeFaqDialog = false;
			this.onChange();
		},
		filterElements(evt) {
			// Check if the target is an input
			const isInput = evt.target.tagName.toLowerCase() === 'input';

			// Function to check if the target or any of its descendants have the specified classes
			const hasTiptapClass = (element) => {
				// Check if the element has either of the two classes
				if(element.classList && (element.classList.contains('tiptap-vuetify-editor__content'))) {
					return true;
				}

				// Use querySelector to check within the element for any descendant with the classes
				return element.querySelector('.tiptap-vuetify-editor__content') !== null;
			};

			// Check if the event target or its descendants have the .tiptap-vuetify-editor__content class
			const isWithinTiptapEditor = hasTiptapClass(evt.target);

			return isInput || isWithinTiptapEditor;
		},
		onChange() {
			this.$emit('change', [...this.faqData]);
		},
		onDragStart() {
			window.EventBus.$emit('faq-drag-start');
			this.disableWysiwygs = true;
		},
		onDragEnd() {
			window.EventBus.$emit('faq-drag-end');
			this.disableWysiwygs = false;

			// // Restore the original contenteditable states
			// this.originalContentEditableStates.forEach(item => {
			// 	item.element.setAttribute('contenteditable', item.contentEditable);
			// });
			//
			// // Clear the stored states
			// this.originalContentEditableStates = [];

			this.onChange();
		},
		removeFaq(i) {
			this.removeFaqDialog = i;
		},
		updateFaqs(newValue) {
			this.faqData = helpers.convertKeysToBoolean([...newValue], [
				'mobile',
				'readOnly',
				'showOnMobilePass'
			]);

			const defaultQuestions = {
				'how-to-add-pass-to-my-phone': { 'question': 'How do I add the pass to my phone?' },
				'how-to-enable-location-services': { 'question': 'How do I enable location services?' },
			};

			this.faqData = this.faqData.map(item => {
				const uid = uuidV4();
				if(item.code) {
					const newQuestion = defaultQuestions[item.code] || {};
					const newAnswer = { answer: '<<The answer is not editable and depends on the device>>' };
					return { ...item, uid, ...newQuestion, ...newAnswer };
				}
				return { ...item, uid };
			});
		}
	},
	watch: {
		faqs(newValue) {
			this.updateFaqs(newValue);
		},
		selectAll(newValue) {
			this.faqData
					.filter(faq => !faq.readOnly)
					.forEach(faq => faq.showOnMobilePass = newValue);
			this.onChange();
		}
	}
}

function Faq(options) {
	this.answer = options.question || '';
	this.question = options.answer || '';
	this.showOnMobilePass = options.showOnMobilePass || false;
  this.uid = uuidV4();
}
</script>

<style lang="scss" scoped>
.sortables > span > div {
	margin: 0 -1rem !important;
	padding: 1rem;
	border-top: solid 1px rgba(0, 0, 0, 0.096);

	&:hover {
		background-color: #f0f8f0 !important;
	}
}
</style>
